import { useCallback, useEffect, useRef } from 'react';
import Logger from '../utils/Logger';
import { useUserPreferences } from './useUserPreferences';

export type SoundOption = 'notification-1' | 'notification-2' | 'none';

/** Which order stream an alert belongs to — each has its own sound, mute and volume. */
export type OrderChannel = 'deeliver' | 'uber';

const SOUND_PATHS: Record<Exclude<SoundOption, 'none'>, string> = {
  'notification-1': '/sounds/notification-1.mp3',
  'notification-2': '/sounds/notification-2.mp3',
};

const PREF_KEY = { deeliver: 'deeliverSound', uber: 'uberSound' } as const;

/**
 * Hook for managing order notification sounds for one channel.
 * Deeliver app orders default to Sound 2, Uber Eats orders to Sound 1.
 * Settings are persisted to the backend via useUserPreferences.
 */
export function useOrderSound(channel: OrderChannel) {
  const { preferences, updatePreferences } = useUserPreferences();
  const key = PREF_KEY[channel];
  const { alert: sound, muted, volume } = preferences[key];

  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Rebuild audio element when sound selection changes
  useEffect(() => {
    if (sound === 'none') {
      audioRef.current = null;
      return;
    }

    const audio = new Audio(SOUND_PATHS[sound]);
    audio.preload = 'auto';
    audio.volume = volume / 100;
    audioRef.current = audio;

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [sound]);

  // Update volume on existing audio element when volume changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
    }
  }, [volume]);

  const playSound = useCallback(() => {
    if (muted || !audioRef.current) return;

    setTimeout(() => {
      const audio = audioRef.current;
      if (!audio) return;
      if (!audio.paused) {
        audio.pause();
      }
      audio.currentTime = 0;
      audio.play().catch((err) => {
        Logger.warn('Could not play notification sound:', err.message);
      });
    }, 50);
  }, [muted]);

  const stopSound = useCallback(() => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, []);

  const setSound = useCallback((s: SoundOption) => {
    updatePreferences({ [key]: { alert: s } });
  }, [key, updatePreferences]);

  const toggleMute = useCallback(() => {
    updatePreferences({ [key]: { muted: !muted } });
  }, [key, muted, updatePreferences]);

  const setMuted = useCallback((m: boolean) => {
    updatePreferences({ [key]: { muted: m } });
  }, [key, updatePreferences]);

  const setVolume = useCallback((v: number) => {
    updatePreferences({ [key]: { volume: Math.max(0, Math.min(100, v)) } });
  }, [key, updatePreferences]);

  return { sound, muted, volume, playSound, stopSound, setSound, toggleMute, setMuted, setVolume };
}
